import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { NumberOfPerPage } from "../utils/globalVariable";
import { formatDateDb } from "../utils/tools";
import { formatDateForInput, formatNumber } from "../utils/utils";

const NUMBER_NFT_PER_PAGE = NumberOfPerPage.lockingManagement;
const queryKey = "locking";

async function fetchLockingStatement(
  currentPage,
  search,
  saleAreaId,
  saleBlockId,
  bookerId,
  status,
  branchId,
  projectId,
  startTime,
  endTime,
  customerId,
  apartmentId,
  isTtkqHdmbs,
  agentId,
) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    };

    if (search !== undefined) options.search = search;
    if (saleAreaId) options.saleAreaId = saleAreaId;
    if (saleBlockId) options.saleBlockId = saleBlockId;
    if (bookerId) options.bookerId = bookerId;
    options.status = ['APPROVED'];
    if (branchId) options.branchId = branchId;
    if (projectId && projectId?.length >0) options.projectId = projectId;
    if (startTime) options.startTime = startTime;
    if (endTime) options.endTime = endTime;
    if (customerId) options.customerId = customerId;
    if (apartmentId?.length) options.apartmentId = apartmentId;
    if (isTtkqHdmbs) options.isTtkqHdmbs = isTtkqHdmbs;
    if (agentId) options.agentId = agentId;

    const queryParams = new URLSearchParams(options).toString();
    let data = await clientAPI("get", `/locking/getLockingStatement?${queryParams}`);

    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries
    );

    const newData = data?.items.map((item, index) => {
      return {
        id: item?._id || "",
        status: item?.status || "",
        statusTtkq: item?.statusTtkq || false,
        statusSalesContract: item?.statusSalesContract || false,

        codeApartment: item?.apartment?.code || "-",
        nameApartment: item?.apartment?.name || "-",
        totalValueApartment: item?.apartment?.totalValue
          ? `${formatNumber(Number(item?.apartment?.totalValue))} VNĐ`
          : "0 VNĐ",
        estimatedServiceFeePrice: item?.apartment?.estimatedServiceFeePrice
          ? `${formatNumber(Number(item?.apartment?.estimatedServiceFeePrice))} VNĐ`
          : "0 VNĐ",

        nameProject: item?.projectObject?.name || "-",

        nameBooker: item?.bookerObject?.fullName || "-",
        saleBlockName: item?.saleArea?.name || "-",
        saleBlockCode: item?.saleArea?.code || "-",
        saleAreaName: item?.saleBlock?.name || "-",
        saleAreaCode: item?.saleBlock?.code || "-",

        nameCustomer: item?.customerObject?.name || "-",
        edit: {
          id: item?._id || "",
          note: item?.note || "",
          nameBooker: item?.nameBooker || "",
          projectId: item?.projectId || "",
          saleOpeningEventId: item?.saleOpeningEventId || "",
          bookingId: item?.bookingId || "",
          customerId: item?.customerId || "",
          price: item?.price || "",
          bookingTime: item?.bookingTime
            ? formatDateForInput(item?.bookingTime)
            : "",
          apartmentId: item?.apartmentId || "",
          paymentMethodId: item?.paymentMethodId || "",
          bookerId: item?.bookerId || "",
          createdAt: item?.createdAt ? formatDateDb(item?.createdAt) : "-",
          images: item?.images,
        },
        detail: {
          id: item?._id || "",
          status: item?.status || "",
          saleOpeningEvent: item?.saleOpeningEvent?.code || "",
          booking: item?.booking?.code || "",
          note: item?.note || "",
          nameBooker: item?.bookerObject?.fullName || "",
          createdAt: item?.createdAt ? formatDateDb(item?.createdAt) : "-",
          saleBlockName: item?.saleArea?.name || "-",
          saleBlockCode: item?.saleArea?.code || "-",
          saleAreaName: item?.saleBlock?.name || "-",
          saleAreaCode: item?.saleBlock?.code || "-",
          project: item?.project?.name || "",
          customer: item?.customerObject?.name || "",
          price: item?.price
            ? `${formatNumber(Number(item?.price))} VNĐ`
            : "0 VNĐ",
          bookingTime: item?.bookingTime ? formatDateDb(item?.bookingTime) : "",
          apartment: item?.apartmentObject?.name || "",
          codeApartment: item?.apartmentObject?.code || "",
          apartments: item?.apartment,
          paymentMethod: item?.paymentMethod?.name || "",
          images: item?.images,
        },
      };
    });

    return { data: newData, totalPages, entries, startEntry, endEntry };
  } catch (error) {
    console.error("Error fetching checkIn data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useLockingStatement(
  search,
  saleAreaId,
  saleBlockId,
  bookerId,
  status,
  branchId,
  projectId,
  startTime,
  endTime,
  customerId,
  apartmentId,
  isTtkqHdmbs,
  agentId,
) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [
      queryKey,
      currentPage,
      search,
      saleAreaId,
      saleBlockId,
      bookerId,
      status,
      branchId,
      projectId,
      startTime,
      endTime,
      customerId,
      apartmentId,
      isTtkqHdmbs,
      agentId,
    ],
    queryFn: () =>
      fetchLockingStatement(
        currentPage,
        search,
        saleAreaId,
        saleBlockId,
        bookerId,
        status,
        branchId,
        projectId,
        startTime,
        endTime,
        customerId,
        apartmentId,
        isTtkqHdmbs,
        agentId,
      ),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    lockingManagementData: data?.data,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}
